import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

import { t, type StringKey } from '../lib/i18n';
import type { Confidence } from '../lib/types';
import { useAppStore } from '../store';

interface ConfidenceBadgeProps {
  confidence: Confidence;
  label?: string;
}

const STYLES: Record<Confidence, string> = {
  high: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30',
  medium: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30',
  low: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30',
};

const ICONS: Record<Confidence, typeof CheckCircle2> = {
  high: CheckCircle2,
  medium: AlertTriangle,
  low: XCircle,
};

const I18N_KEYS: Record<Confidence, StringKey> = {
  high: 'confidence.high',
  medium: 'confidence.medium',
  low: 'confidence.low',
};

export function ConfidenceBadge({ confidence, label }: ConfidenceBadgeProps) {
  const lang = useAppStore((s) => s.lang);
  const localised = t(lang, I18N_KEYS[confidence]);
  const text = label ?? localised;
  const Icon = ICONS[confidence];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STYLES[confidence]}`}
      aria-label={`Confidence: ${localised}`}
      title={`Confidence: ${localised}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span>{text}</span>
    </span>
  );
}
